import React, { useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./CommunitiesPage.module.css";
import { communityRoleStore } from "../../Data/store";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { useV2CommunityRoles } from "../../security/v2/useV2CommunityRoles";
import CommunityCard from "../../Components/CommunityCard/CommunityCard";
import {
  buildEntityMap,
  buildParentMap,
  type CommunityEntity,
  type CommunityRelationship,
} from "../../Components/CommunityCard/communityCardData";

// DEV-1447 — the card, and the flag/ancestry/hero helpers behind it, moved to
// the shared <CommunityCard>. This page and the Discover Communities catalog
// now render the same component, so they cannot drift.
export type { CommunityEntity, CommunityRelationship };

interface Props {
  /** Live community entities from the API (seed-shape objects). */
  entities?: CommunityEntity[];
  /** Parent→child relationships for building the hierarchy tree. */
  relationships?: CommunityRelationship[];
  /** Current user's GUID for filtering memberships and roles. */
  currentUserGuid?: string;
  onOpenCommunity: (communityGuid: string) => void;
}

const CommunitiesPage: React.FC<Props> = ({
  entities: propEntities,
  relationships: propRelationships,
  currentUserGuid,
  onOpenCommunity,
}) => {
  const { t } = useAppTranslation('pages');
  const [query, setQuery] = useState("");

  const upperUser = (currentUserGuid || '').toUpperCase();

  // Build lookup maps
  const entityMap = useMemo(() => buildEntityMap(propEntities), [propEntities]);
  const parentMap = useMemo(() => buildParentMap(propRelationships), [propRelationships]);

  // All active, public communities (workshops excluded — see DEV-961).
  // Workshops are technically a community per ADR-006 (needed for the
  // project-adoption flow's community_id swap) but are functionally
  // private to their owner and accessed via the user's profile, not
  // by browsing the Communities list. The visibility=private check
  // already excludes them for non-members; the personal_owner check
  // additionally excludes them for the owner themselves.
  const allActive = useMemo(() =>
    (propEntities || []).filter(
      (c) =>
        c.is_active !== false
        && c.visibility !== "private"
        && !c.personal_owner_user_guid
    ),
    [propEntities]
  );

  // DEV-971 — securityUsers for the 2FA check below. DEV-1372 —
  // communityMemberships is THE membership read source; the rows are
  // async-hook values so every memo reading them lists them in deps.
  const { securityUsers, communityMemberships } = useApiSecurityData();

  // User's active membership community GUIDs.
  //
  // DEV-1372 slice 1 — single source of truth. The DEV-1020 ad-hoc union
  // (home + operational security_users columns unioned in here) is
  // retired: migration 259 backfilled those declarations into
  // community_memberships rows (type home/operating) and Account
  // Settings dual-writes them server-side, so the one membership read
  // now answers "my member communities" by itself.
  const memberGuids = useMemo(() => {
    const set = new Set<string>();
    if (!upperUser) return set;

    for (const m of communityMemberships || []) {
      if (
        String((m as any).user_guid || '').toUpperCase() === upperUser &&
        String((m as any).status || 'active').toLowerCase() === 'active'
      ) {
        set.add(String((m as any).community_entity_guid || '').toUpperCase());
      }
    }
    return set;
  }, [upperUser, communityMemberships]);

  const is2FAEnabled = useMemo(() => {
    const me = (currentUserGuid || '').toUpperCase();
    if (!me) return false;
    const user = (securityUsers || []).find(
      (u: any) => String(u.user_guid).toUpperCase() === me,
    );
    return !!user?.is_two_factor_enabled;
  }, [securityUsers, currentUserGuid]);

  // DEV-971 — V2 evaluator pass over permission_grants. Returns
  // capabilities by community GUID. Combined with the legacy V1 read below
  // so grants made via either the legacy CommunitySettingsModal Audience
  // tab (V1) or the new V2 Permissions panel both surface here. Drop the
  // V1 branch after DEV-876.
  const { capsByCommunity: v2Caps } = useV2CommunityRoles({
    userGuid: currentUserGuid || '',
    is2FAEnabled,
    entities: propEntities,
    relationships: propRelationships,
  });

  // User's role-assigned community GUIDs (excluding ones where user is also a member).
  // V1 source: communityRoleStore (community_role_assignments hydrated into store).
  // V2 source: permission_grants where evaluator returns any community-scope
  // capability — community:view is the floor, held by every assignable bundle.
  const contributingGuids = useMemo(() => {
    const set = new Set<string>();
    if (!upperUser) return set;

    // V1 read
    for (const r of communityRoleStore) {
      const guid = String(r.community_entity_guid || '').toUpperCase();
      if (
        String(r.user_guid || '').toUpperCase() === upperUser &&
        !memberGuids.has(guid)
      ) {
        set.add(guid);
      }
    }

    // V2 read — any community-scope capability via grant (direct or
    // cascaded). community:view is held by every assignable bundle so it's
    // the right "do I have any role here?" floor.
    for (const [guid, caps] of v2Caps) {
      if (caps.has('community:view') && !memberGuids.has(guid)) {
        set.add(guid);
      }
    }

    return set;
  }, [upperUser, memberGuids, v2Caps]);

  // Split communities into sections
  const myCommunities = useMemo(() => {
    const list = allActive.filter((c) => memberGuids.has(c.entity_guid.toUpperCase()));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => String(c.name || "").toLowerCase().includes(q));
  }, [allActive, memberGuids, query]);

  const contributingTo = useMemo(() => {
    const list = allActive.filter((c) => contributingGuids.has(c.entity_guid.toUpperCase()));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => String(c.name || "").toLowerCase().includes(q));
  }, [allActive, contributingGuids, query]);

  const hasAnything = myCommunities.length > 0 || contributingTo.length > 0;

  // ── Render a community card ──
  // DEV-1447 — shared <CommunityCard>. The Follow CTA is suppressed here:
  // these are communities the user already belongs to or holds a role in, so
  // a Follow affordance adds noise rather than signal. The Discover catalog
  // (which serves strangers) turns it on.
  const renderCard = (c: CommunityEntity) => (
    <CommunityCard
      key={c.entity_guid}
      entity={c}
      entityMap={entityMap}
      parentMap={parentMap}
      onOpen={onOpenCommunity}
      showFollow={false}
    />
  );

  return (
    <div className={`${styles.page} pageContainer`}>
      <div className={styles.header}>
        <input
          className={styles.search}
          placeholder={t('communities.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {!hasAnything && !query && (
        <div className={styles.emptyState}>
          {t('communities.empty')}
        </div>
      )}

      {!hasAnything && query && (
        <div className={styles.emptyState}>
          {t('communities.noMatch', { query })}
        </div>
      )}

      {/* ── My Communities ── */}
      {myCommunities.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('communities.myCommunities')}
            <span className={styles.sectionCount}>{myCommunities.length}</span>
          </h2>
          <div className={styles.grid}>
            {myCommunities.map(renderCard)}
          </div>
        </section>
      )}

      {/* ── Contributing To ── */}
      {contributingTo.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('communities.contributingTo')}
            <span className={styles.sectionCount}>{contributingTo.length}</span>
          </h2>
          <div className={styles.grid}>
            {contributingTo.map(renderCard)}
          </div>
        </section>
      )}

    </div>
  );
};

export default CommunitiesPage;
